import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useCategoryOptions,
  useSubcategoryOptions,
  useUpdatePlanMutation,
} from "../../../../hooks";
import { useParams } from "react-router-dom";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface CategoryFormData {
  category?: string;
  subcategory?: string;
  categoryCode?: string;
  subCategoryCode?: string;
}

const ConsumerCategoriesStep = ({
  onNext,
  onPrevious,
  isFirstStep,
  isLastStep,
  currentStepIndex = 1,
  totalSteps = 4,
  data = {},
  onDataChange,
  setValidationError,
  clearValidationError,
}: {
  onNext: () => void;
  onPrevious: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
  currentStepIndex?: number;
  totalSteps?: number;
  data?: CategoryFormData;
  onDataChange: (data: any) => void;
  setValidationError?: (msg: string) => void;
  clearValidationError?: () => void;
}) => {
  console.log("ConsumerCategoriesStep rendered", data);
  const dataLoadedRef = useRef(false);
  const [selectedCategoryCode, setSelectedCategoryCode] = useState("");
  const [selectedSubcategoryCode, setSelectedSubcategoryCode] = useState("");

  const { planId, action } = useParams<{ planId?: string; action: string }>();
  const parsedPlanId = planId ? Number(planId) : null;
  const updatePlanMutation = useUpdatePlanMutation(parsedPlanId);
  const utilityId = useRemoteUtilityId();

  const { data: categories = [], isLoading: loadingCategories } =
    useCategoryOptions();
  const { data: subcategories = [], isLoading: loadingSubcategories } =
    useSubcategoryOptions(selectedCategoryCode);

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ label: c.name, value: c.code })),
    [categories]
  );

  const subcategoryOptions = useMemo(
    () => subcategories.map((sc) => ({ label: sc.name, value: sc.code })),
    [subcategories]
  );

  const consumerCategoriesFields: FormField[] = useMemo(
    () => [
      {
        name: "category",
        type: "select",
        label: "Category",
        required: true,
        placeholder: "Select category",
        options: categoryOptions,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
        helperText: "Select the main category for this plan.",
      },
      {
        name: "subcategory",
        type: "select",
        label: "Subcategory",
        required: true,
        placeholder: selectedCategoryCode
          ? "Select subcategory"
          : "Select category first",
        options: subcategoryOptions,
        disabled: !selectedCategoryCode || loadingSubcategories,
        classes: {
          container: "w-full mb-4",
          label: "block text-sm font-medium text-gray-700 mb-1",
          select:
            "w-full h-10 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-colors bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed",
          error: "text-red-600 text-sm mt-1",
          helperText: "text-gray-500 text-xs mt-1",
        },
        helperText: selectedCategoryCode
          ? "Select the specific subcategory for this plan."
          : "Please select a category first to load subcategories.",
      },
    ],
    [
      categoryOptions,
      subcategoryOptions,
      selectedCategoryCode,
      loadingSubcategories,
    ]
  );

  const form = FormService.createForm({
    fields: consumerCategoriesFields,
    mode: "onChange",
    validateOnMount: false,
  });

  // Data restoration effect - similar to WorkflowTriggerStep pattern
  useEffect(() => {
    const shouldLoadData =
      !dataLoadedRef.current && data && Object.keys(data).length > 0;

    if (shouldLoadData) {
      try {
        console.log("🔁 Loading data into form:", data);

        // Set the local state first
        if (data.categoryCode) {
          setSelectedCategoryCode(data.categoryCode);
        }
        if (data.subCategoryCode) {
          setSelectedSubcategoryCode(data.subCategoryCode);
        }

        // Then set form values
        const formData = {
          category: data.categoryCode || data.category || "",
          subcategory: data.subCategoryCode || data.subcategory || "",
        };

        const currentFormValues = form.getValues();
        const shouldUpdateForm =
          currentFormValues.category !== formData.category ||
          currentFormValues.subcategory !== formData.subcategory;

        if (shouldUpdateForm) {
          console.log("🔄 Setting form values:", formData);
          // Set each value individually to ensure they're properly set
          if (formData.category) {
            form.setValue("category", formData.category);
          }
          if (formData.subcategory) {
            form.setValue("subcategory", formData.subcategory);
          }
        }

        dataLoadedRef.current = true;
      } catch (error) {
        console.error("Error loading data:", error);
        dataLoadedRef.current = true;
      }
    }
  }, [data, form]);

  // Effect to handle subcategory restoration after subcategory options are loaded
  useEffect(() => {
    if (
      dataLoadedRef.current &&
      selectedCategoryCode &&
      data?.subCategoryCode &&
      subcategoryOptions.length > 0 &&
      selectedSubcategoryCode &&
      !form.getValues().subcategory
    ) {
      console.log(
        "🔄 Setting subcategory after options loaded:",
        data.subCategoryCode
      );
      form.setValue("subcategory", data.subCategoryCode);
    }
  }, [
    selectedCategoryCode,
    subcategoryOptions.length,
    data?.subCategoryCode,
    selectedSubcategoryCode,
    form,
  ]);

  // Reset dataLoaded ref when component unmounts
  useEffect(() => {
    return () => {
      dataLoadedRef.current = false;
    };
  }, []);

  const handleSubmit = async (formData: any) => {
    try {
      clearValidationError?.();

      const payload = {
        category: formData.category,
        subcategory: formData.subcategory,
        categoryCode: formData.category,
        subCategoryCode: formData.subcategory,
        remote_utility_id: utilityId,
        current_step:2,
      };

      console.log("🚀 Submitting Step 2 Payload:", payload);

      await updatePlanMutation.mutateAsync(payload);

      onDataChange?.(payload);
      onNext?.();
    } catch (error) {
      console.error("❌ Submission failed", error);
      setValidationError?.("An error occurred while saving. Please try again.");
    }
  };

  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Clear field errors if they exist
      if (FormService.getFieldError(form, fieldName)) {
        form.clearErrors(fieldName);
      }

      console.log(`🔄 Field changed: ${fieldName} = ${value}`);

      if (fieldName === "category") {
        setSelectedCategoryCode(value);
        setSelectedSubcategoryCode(""); // Clear subcategory code
        // Clear subcategory form value when category changes
        form.setValue("subcategory", "");

        onDataChange?.({
          ...data,
          categoryCode: value,
          category: value,
          subCategoryCode: "",
          subcategory: "",
        });
      } else if (fieldName === "subcategory") {
        setSelectedSubcategoryCode(value);
        onDataChange?.({
          ...data,
          subCategoryCode: value,
          subcategory: value,
        });
      } else {
        const updatedData = { ...data, [fieldName]: value };
        onDataChange?.(updatedData);
      }
    },
    [data, onDataChange, form]
  );

  const handleReset = useCallback(() => {
    form.reset();
    setSelectedCategoryCode("");
    setSelectedSubcategoryCode("");
    onDataChange?.({
      categoryCode: "",
      subCategoryCode: "",
      category: "",
      subcategory: "",
    });
    dataLoadedRef.current = false;
  }, [form, onDataChange]);

  const renderCustomFooter = useCallback(
    () => (
      <div className="space-y-4">
        {selectedCategoryCode && loadingSubcategories && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
            <div className="flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="text-sm text-blue-800">
                Loading subcategories...
              </span>
            </div>
          </div>
        )}

        <div className="flex justify-between pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            disabled={isFirstStep}
            className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
          >
            <ChevronLeft className="inline w-4 h-4 mr-1" />
            Previous
          </button>
          <button
            type="submit"
            disabled={!selectedCategoryCode || loadingSubcategories}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
            <ChevronRight className="inline w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    ),
    [selectedCategoryCode, loadingSubcategories, isFirstStep, onPrevious]
  );

  return (
    <div className="space-y-6">
      <DynamicForm
        fields={consumerCategoriesFields}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        onFieldChange={handleFieldChange}
        loading={loadingCategories}
        config={{
          showProgress: false,
          submitButtonText: "Next",
          showResetButton: false,
          resetOnSubmit: false,
          classes: {
            fieldContainer: "",
            buttonContainer: "hidden",
            fieldGroup: "",
          },
        }}
        renderCustomFooter={renderCustomFooter}
        className=""
        style={{
          background: "transparent",
          border: "none",
          boxShadow: "none",
        }}
      />
    </div>
  );
};

export default ConsumerCategoriesStep;
